<template>
  <InputCombobox
    :model-value="modelValue"
    :items="selectableItems"
    items-key-name="id"
    v-bind="inheritedAttrs"
    :disabled="disabled"
    :disabled-items="disabledItems"
    :creation-parameters="formCreationParameters"
    :auto-select-newly-created-model="requireApprovalMode ? false : autoSelectNewlyCreatedModel"
    @update:model-value="onSelectionChanged"
    @item-created="onItemSaved"
    @item-updated="onItemSaved"
    @query-changed="onQueryChanged"
    :is-loading="isSearchingKVK"
    search-thoroughly
    new-item-text="Een ander bedrijf toevoegen"
    ref="combobox"
    class="known-business-entity-combobox"
  >
    <template v-if="$slots['before-input']" #before-input>
      <slot name="before-input" />
    </template>
    <template #custom-options="{ disabled, onMouseDownLeft, onMouseUpLeft }">
      <div v-if="kvkSearchError" role="status" class="p-2 text-sm">
        {{ kvkSearchError }}
        <button
          type="button"
          class="text-primary underline"
          :disabled="disabled"
          @mousedown.prevent
          @click.stop="scheduleSearchKVK(query)"
        >
          Opnieuw proberen
        </button>
      </div>
      <div v-if="unconfiguredOptions.length > 0" class="gray-lighten-2 p-2 font-medium">
        <span>Accordering nog niet ingesteld</span>
      </div>
      <CustomComboboxOption
        v-for="option in unconfiguredOptions"
        :key="option.item.id"
        :wrapper="option"
        :text="option.item.name"
        :disabled="disabled || isCompanyDisabled(option.item)"
        @mousedown.left="onMouseDownLeft"
        @mouseup.left="onMouseUpLeft"
      >
        <template #after-text>
          <MultiBadge class="gray-lighten-1! ml-2" size="sm">Accordering instellen</MultiBadge>
        </template>
      </CustomComboboxOption>

      <div v-if="wrappedKVKResults.length > 0" class="gray-lighten-2 p-2 font-medium">
        <span>Resultaten uit het handelsregister</span>
      </div>

      <CustomComboboxOption
        v-for="wrappedKVKResult in wrappedKVKResults"
        :key="wrappedKVKResult.item.id"
        :wrapper="wrappedKVKResult"
        @mousedown.left="onMouseDownLeft"
        @mouseup.left="onMouseUpLeft"
        :disabled="disabled"
      >
        <template #text="{ active, selected }">
          <div class="min-w-0">
            <span :class="['block truncate', selected && 'font-semibold']">
              {{ wrappedKVKResult.item.naam }}
            </span>
            <div class="truncate text-sm" :class="active ? 'text-gray-lighten-2/50' : ''">
              {{ wrappedKVKResult.item.kvkNummer }} -
              {{ wrappedKVKResult.item.adres?.binnenlandsAdres?.straatnaam }},
              {{ wrappedKVKResult.item.adres?.binnenlandsAdres?.plaats }}
            </div>
          </div>
        </template>
        <template #after-text>
          <MultiBadge color="primary-lighten-1" class="text-nowrap" size="xs">
            <FontAwesomeIcon :icon="['fas', 'plus']" size="xs" />
            <span>Bedrijf aanmaken</span>
          </MultiBadge>
        </template>
      </CustomComboboxOption>

      <div v-if="totalKVKResultsCount > 5" class="gray-lighten-2 px-2 py-1.5 text-xs">
        Eerste 5 van {{ totalKVKResultsCount }} resultaten. Verfijn je zoekopdracht om een specifiek
        bedrijf te vinden.
      </div>
    </template>
    <template #loading-text>Handelsregister doorzoeken...</template>
  </InputCombobox>
</template>

<script setup lang="ts">
// Do not inherit all attrs because v-model will be inherited by InputCombobox, which we don't want
import { computed, nextTick, ref, useAttrs, useTemplateRef } from "vue"
import InputCombobox from "@/components/inputs/input_combobox/InputCombobox.vue"
import { KnownBusinessEntity } from "@/models/known-business-entity.model.ts"
import MultiBadge from "@/components/misc/MultiBadge.vue"
import CustomComboboxOption from "@/components/inputs/input_combobox/CustomComboboxOption.vue"
import { CustomComboboxOptionWrapper } from "@/components/inputs/input_combobox/CustomComboboxOptionWrapper.ts"
import { useModelFormStore } from "@/store/model-forms.store.ts"
import { useKVKSearch } from "./use-kvk-search"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { ModelFactory } from "@/factories/model-factory.ts"
import { useKnownBusinessEntityStore } from "@/store/known-business-entities.store"
import {
  getBusinessRegistrationKey,
  getKnownBusinessEntityRegistrationKey
} from "@/helper/known-business-entity-registration"

const props = withDefaults(
  defineProps<{
    items: KnownBusinessEntity[]
    requireApprovalMode?: boolean
    disabled?: boolean
    disabledItems?: KnownBusinessEntity[]
    isSelectable?: (company: KnownBusinessEntity) => boolean
    creationParameters?: Record<string, unknown>
    autoSelectNewlyCreatedModel?: boolean
  }>(),
  {
    requireApprovalMode: false,
    disabledItems: () => [],
    creationParameters: () => ({}),
    autoSelectNewlyCreatedModel: true
  }
)

defineOptions({
  inheritAttrs: false
})
const attrs = useAttrs()

const inheritedAttrs = computed((): Record<string, unknown> => {
  const _inheritedAttrs: Record<string, unknown> = { ...attrs }
  delete _inheritedAttrs["v-model"] // We want all attrs to be inherited, except v-model
  delete _inheritedAttrs["v-on:input"] // We want all attrs to be inherited, except v-model
  return _inheritedAttrs
})

const modelValue = defineModel<KnownBusinessEntity | null>()

const query = ref("")
const selectableItems = computed(() =>
  props.requireApprovalMode ? props.items.filter((company) => company.approvalMode) : props.items
)
const formCreationParameters = computed(() => ({
  ...props.creationParameters,
  ...(props.requireApprovalMode ? { requireApprovalMode: true, focusApprovalSettings: true } : {})
}))

function isCompanyDisabled(company: KnownBusinessEntity) {
  return (
    props.disabled ||
    props.disabledItems.some((item) => item.id === company.id) ||
    (props.isSelectable && !props.isSelectable(company))
  )
}

function onSelectionChanged(company: KnownBusinessEntity | null) {
  if (
    props.requireApprovalMode &&
    company &&
    (!company.approvalMode || isCompanyDisabled(company))
  ) {
    return
  }
  modelValue.value = company
  cancelKVKSearch()
}

async function selectConfiguredCompany(company: KnownBusinessEntity) {
  // Saving updates the shared company store. Wait for the caller's disabled list to refresh.
  await nextTick()
  if (!company.approvalMode || isCompanyDisabled(company)) return
  combobox.value?.selectItem(company)
}

function onItemSaved(company: KnownBusinessEntity) {
  if (props.requireApprovalMode) void selectConfiguredCompany(company)
}

const unconfiguredOptions = computed(() => {
  if (!props.requireApprovalMode) return []
  const searchTerms = query.value.toLocaleLowerCase().split(" ")
  return props.items
    .filter(
      (company) =>
        !company.approvalMode &&
        searchTerms.every((term) => company.name.toLocaleLowerCase().includes(term))
    )
    .sort((a, b) => a.name.localeCompare(b.name))
    .slice(0, 50)
    .map(
      (company) =>
        new CustomComboboxOptionWrapper(company, () => {
          if (isCompanyDisabled(company)) return
          modelFormStore.openForm({
            model: company,
            creationParameters: formCreationParameters.value,
            saveHandle: selectConfiguredCompany
          })
        })
    )
})
const {
  results: KVKSearchResults,
  isSearching: isSearchingKVK,
  error: kvkSearchError,
  schedule: scheduleSearchKVK,
  cancel: cancelKVKSearch
} = useKVKSearch()
const knownBusinessEntityStore = useKnownBusinessEntityStore()
const existingRegistrations = computed(
  () =>
    new Set(
      [...knownBusinessEntityStore.knownBusinessEntities, ...props.items]
        .map(getKnownBusinessEntityRegistrationKey)
        .filter((key): key is string => Boolean(key))
    )
)
const availableKVKResults = computed(() =>
  KVKSearchResults.value.filter(
    (item) =>
      !existingRegistrations.value.has(getBusinessRegistrationKey("NL", item.kvkNummer) ?? "")
  )
)
const totalKVKResultsCount = computed(() => availableKVKResults.value.length)
const combobox = useTemplateRef<typeof InputCombobox>("combobox")

function onQueryChanged(value: string) {
  query.value = value
  scheduleSearchKVK(value)
}

const modelFormStore = useModelFormStore()
const wrappedKVKResults = computed<CustomComboboxOptionWrapper<any>[]>(() => {
  return availableKVKResults.value.slice(0, 5).map((result) => {
    return new CustomComboboxOptionWrapper(result, () => {
      modelFormStore.openForm({
        model: ModelFactory.createModel(KnownBusinessEntity),
        textInput: query.value,
        creationParameters: {
          ...formCreationParameters.value,
          // Pass the selected KVK result to creationParameters so it is automatically selected in the combobox.
          KVKResult: result
        },
        saveHandle: props.requireApprovalMode ? selectConfiguredCompany : combobox.value?.selectItem // Auto-select newly created item
      })
    })
  })
})
</script>

<style scoped></style>
